import React, { useEffect, useRef } from "react";
import CoursesCardSquare from "./Cards/CoursesCardSquare";
import HorizontalScroll from "./HorizontalScroll";
import { DisplayCourse } from "@/hooks/useCourses";
import { useCompletedLessons } from "@/hooks/useCompletedLessons";

interface CourseHighlightProps {
  courses: DisplayCourse[];
  onClick: (courseId: number) => void;
}

const CourseHighlight: React.FC<CourseHighlightProps> = ({
  courses,
  onClick,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { completedLessons } = useCompletedLessons();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft = 0;
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [courses]);

  // Create a map of courseIds from completedLessons for faster lookups
  // map of number and int, order of completion
  const courseIdHasCompletedLessons = new Map<number, number>();
  if (completedLessons) {
    completedLessons.forEach((lesson, index) => {
      if (!courseIdHasCompletedLessons.has(lesson.courseId)) {
        courseIdHasCompletedLessons.set(lesson.courseId, index);
      }
    });
  }
  // Keep courses if they have lesson completed
  const coursesWithLessonsCompleted = courses
    .filter((course) => {
      if (course.progress === 100) {
        return false;
      }
      return courseIdHasCompletedLessons.has(course.courseId);
    })
    .sort((a, b) => {
      return (
        (courseIdHasCompletedLessons.get(a.courseId) ?? 0) -
        (courseIdHasCompletedLessons.get(b.courseId) ?? 0)
      );
    });

  // Keep courses that are completed (progress === 100) but put them at the end
  const coursesCompleted = courses.filter((course) => course.progress === 100);
  // if NO courses have lessons completed thegetn show 5 courses that haven't been started
  const coursesNotStarted = courses.filter(
    (course) => !courseIdHasCompletedLessons.has(course.courseId)
  );
  const displayCourses = [...coursesWithLessonsCompleted];
  if (coursesWithLessonsCompleted.length === 0) {
    const sliceEnd = Math.min(coursesNotStarted.length, 3);
    displayCourses.push(...coursesNotStarted.slice(0, sliceEnd));
  }
  displayCourses.push(...coursesCompleted);
  return (
    <HorizontalScroll ref={scrollContainerRef}>
      <div className="flex flex-row gap-4 h-auto min-w-max">
        {displayCourses.map((course) => (
          <CoursesCardSquare
            key={course.courseId}
            emoji={course.icon || "📚"}
            title={course.name}
            subtitle={course.description}
            level={course.level}
            progress={course.progress}
            chapters={`${course.totalChapters} chapters`}
            onClick={() => onClick(course.courseId)}
          />
        ))}
      </div>
    </HorizontalScroll>
  );
};

export default CourseHighlight;
